import { BankOutlined, LockOutlined, UserOutlined } from "@ant-design/icons";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Alert, Button, Checkbox, Form, Input, Typography } from "antd";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import { ApiClientError } from "@/api/api_client";
import {
  get_login_branding,
  login_user,
  type LoginRequest,
  type LoginResult,
} from "@/features/auth/auth_api";
import { login_page_meta } from "@/pages/login/page_meta";
import styles from "@/pages/login/login_page.module.css";

const { Paragraph, Text, Title } = Typography;
const logo_url = `${import.meta.env.BASE_URL}images/logo/logo.png`;

function get_error_message(error: unknown): string {
  if (error instanceof ApiClientError) {
    return error.message;
  }
  return "登录失败，请稍后重试";
}

export function LoginPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const query_client = useQueryClient();
  const [initial_password_result, set_initial_password_result] =
    useState<LoginResult | null>(null);
  const login_branding_query = useQuery({
    queryKey: ["login-branding"],
    queryFn: get_login_branding,
    staleTime: 5 * 60 * 1000,
  });
  const company_name =
    login_branding_query.data?.company_name ||
    (login_branding_query.isPending
      ? t("login.company_loading")
      : t("login.company_fallback"));

  useEffect(() => {
    document.title = login_page_meta.document_title;
  }, []);

  const login_mutation = useMutation({
    mutationFn: login_user,
    onSuccess: (result) => {
      if (result.is_initial_password) {
        set_initial_password_result(result);
        return;
      }
      query_client.clear();
      navigate("/", { replace: true });
    },
  });

  const submit_login = (values: LoginRequest) => {
    set_initial_password_result(null);
    login_mutation.reset();
    login_mutation.mutate(values);
  };

  const continue_to_app = () => {
    query_client.clear();
    navigate("/", { replace: true });
  };

  return (
    <main className={styles.login_shell}>
      <section className={styles.story_panel} aria-label="产品介绍">
        <div className={styles.story_content}>
          <div className={styles.brand_lockup}>
            <img
              className={styles.brand_logo}
              src={logo_url}
              alt={login_page_meta.product_name}
            />
            <div>
              <strong>{login_page_meta.product_name}</strong>
              <span>{login_page_meta.product_caption}</span>
            </div>
          </div>

          <div className={styles.story_copy}>
            <Text className={styles.eyebrow!}>
              {login_page_meta.wordmark}
            </Text>
            <Title level={1}>{login_page_meta.slogan}</Title>
          </div>

          <Text className={styles.story_footer!}>
            {login_page_meta.copyright}
          </Text>
        </div>
        <div className={styles.decorative_grid} aria-hidden="true" />
      </section>

      <section className={styles.form_panel}>
        <div className={styles.login_card}>
          <div className={styles.mobile_brand}>
            <img
              className={styles.brand_logo}
              src={logo_url}
              alt=""
              aria-hidden="true"
            />
            <div>
              <strong>{login_page_meta.product_name}</strong>
              <span>{login_page_meta.product_caption}</span>
            </div>
          </div>

          <div
            className={styles.tenant_identity}
            role="group"
            aria-label={t("login.company_label")}
          >
            <span className={styles.tenant_icon} aria-hidden="true">
              <BankOutlined />
            </span>
            <div className={styles.tenant_copy}>
              <Text>{t("login.company_label")}</Text>
              <strong aria-live="polite">{company_name}</strong>
            </div>
          </div>

          <div className={styles.form_heading}>
            <Title level={2}>{t("login.title")}</Title>
            <Paragraph className={styles.form_subtitle!}>
              {t("login.subtitle")}
            </Paragraph>
          </div>

          {login_mutation.isError ? (
            <Alert
              className={styles.login_alert!}
              title={get_error_message(login_mutation.error)}
              type="error"
              showIcon
            />
          ) : null}

          {initial_password_result ? (
            <Alert
              className={styles.login_alert!}
              title={t("login.initial_password_title")}
              description={initial_password_result.message}
              type="warning"
              showIcon
              action={
                <Button type="primary" onClick={continue_to_app}>
                  {t("login.continue_to_app")}
                </Button>
              }
            />
          ) : (
            <Form<LoginRequest>
              className={styles.login_form}
              initialValues={{ remember_me: false }}
              requiredMark={false}
              onFinish={submit_login}
              size="large"
            >
              <Form.Item
                className={styles.credential_item!}
                name="username"
                rules={[
                  {
                    required: true,
                    whitespace: true,
                    message: t("login.username_required"),
                  },
                ]}
              >
                <Input
                  prefix={<UserOutlined />}
                  aria-label={t("login.username")}
                  placeholder={t("login.username_placeholder")}
                  autoComplete="username"
                  autoFocus
                />
              </Form.Item>

              <Form.Item
                className={styles.credential_item!}
                name="password"
                rules={[
                  {
                    required: true,
                    message: t("login.password_required"),
                  },
                ]}
              >
                <Input.Password
                  prefix={<LockOutlined />}
                  aria-label={t("login.password")}
                  placeholder={t("login.password_placeholder")}
                  autoComplete="current-password"
                />
              </Form.Item>

              <div className={styles.form_options}>
                <Form.Item name="remember_me" valuePropName="checked" noStyle>
                  <Checkbox>{t("login.remember_me")}</Checkbox>
                </Form.Item>
              </div>

              <Button
                className={styles.submit_button!}
                type="primary"
                htmlType="submit"
                loading={login_mutation.isPending}
                block
              >
                {t("login.submit")}
              </Button>
            </Form>
          )}
        </div>
      </section>
    </main>
  );
}
